import { useQueryClient } from 'react-query'
import toast from 'react-hot-toast'
import { useAuthStore } from '../stores/authStore'
import { useOrganizationStore } from '../stores/organizationStore'
import { canMeaningfullySwitchOrgs } from '../utils/orgSwitching'
import useThemeStore from '../stores/themeStore'
import { useFeatureFlag } from '../hooks/useFeatureFlag'
import useFeedbackStore from '../stores/feedbackStore'
import {
  LogOut,
  Settings,
  Sun,
  Moon,
  Monitor,
  Building2,
  MessageSquare,
  Menu,
  X,
  ChevronDown,
  Check,
  Users,
  User,
  Gift,
  HelpCircle
} from 'lucide-react'
import { Avatar } from './profile'
import { useState, useRef, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useBranding } from '../stores/brandingStore'
import DeaverisLogo from '../assets/deaveris.svg'
import useSidebarStore from '../stores/sidebarStore'
import NotificationBell from './NotificationBell'
import MessagesBell from './MessagesBell'
import { getMyReferralCode } from '../services/referralService'
import { createLogger } from '../utils/logger'

const logger = createLogger('Header')

// Clear invite cache on page load to ensure fresh data
// (This runs once when module loads, not on SPA navigation)
sessionStorage.removeItem('referral_invite_count')
sessionStorage.removeItem('referral_invite_count_time')

const Header = () => {
  const navigate = useNavigate()
  const branding = useBranding()
  const queryClient = useQueryClient()
  const { user, logout, isImpersonating } = useAuthStore()
  const { activeOrganization, setActiveOrganization, clearActiveOrganization } =
    useOrganizationStore()
  const { theme, effectiveTheme, toggleTheme } = useThemeStore()
  const { enabled: darkModeEnabled } = useFeatureFlag('dark_mode')
  const openFeedbackModal = useFeedbackStore((state) => state.openFeedbackModal)
  const { isOpen: sidebarOpen, toggle: toggleSidebar } = useSidebarStore()
  const [showDropdown, setShowDropdown] = useState(false)
  const [showOrgSwitcher, setShowOrgSwitcher] = useState(false)
  const orgSwitcherRef = useRef(null)

  // Referral invite state
  const [inviteCount, setInviteCount] = useState(null)
  const [shouldAnimateGift, setShouldAnimateGift] = useState(true)
  const hasVisitedReferrals = localStorage.getItem('referrals_visited') === 'true'

  // Get user's organizations.
  // The "personal" workspace is modeled as the null active-org ("Personal Account"),
  // NOT as a selectable org — mirror OrganizationSwitcher so the header and the
  // profile page agree on what's active.
  const userOrganizations = user?.organizations || []
  const personalOrg = userOrganizations.find((o) => o.type === 'personal')
  const hasPersonalOrg = Boolean(personalOrg)
  const shouldShowPersonalAccount = hasPersonalOrg || userOrganizations.length === 0
  // Display the personal workspace by its real name when it has one.
  const personalLabel = personalOrg?.name || 'Personal Account'
  const nonPersonalOrgs = userOrganizations.filter((o) => o.type !== 'personal')
  // Only surface the switcher when switching actually changes something for this
  // user (elevated role or a branded org); otherwise it's a pointless dropdown.
  const showSwitcher = canMeaningfullySwitchOrgs(userOrganizations)
  // Treat a null active org (or a lingering personal-org selection) as personal mode.
  const inPersonalMode = !activeOrganization || activeOrganization.type === 'personal'
  // Switchable contexts = Personal Account (when applicable) + each non-personal org.
  const switchableCount = (shouldShowPersonalAccount ? 1 : 0) + nonPersonalOrgs.length
  const hasMultipleOrgs = switchableCount > 1

  // Close org switcher when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (orgSwitcherRef.current && !orgSwitcherRef.current.contains(event.target)) {
        setShowOrgSwitcher(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Fetch referral invite count on mount with proper cleanup
  useEffect(() => {
    let isMounted = true
    let cacheTimeout = null

    const fetchInviteCount = async () => {
      // Check sessionStorage cache first (5 minute TTL)
      const cached = sessionStorage.getItem('referral_invite_count')
      const cacheTime = sessionStorage.getItem('referral_invite_count_time')
      const CACHE_TTL = 5 * 60 * 1000 // 5 minutes

      if (cached && cacheTime && Date.now() - parseInt(cacheTime, 10) < CACHE_TTL) {
        if (isMounted) {
          setInviteCount(parseInt(cached, 10))
        }
        return
      }

      try {
        const response = await getMyReferralCode()
        if (isMounted && response.success) {
          const count = response.data.availableInvites ?? 0
          setInviteCount(count)
          // Cache the result
          sessionStorage.setItem('referral_invite_count', count.toString())
          sessionStorage.setItem('referral_invite_count_time', Date.now().toString())
        }
      } catch (err) {
        logger.error('Failed to fetch invite count:', err)
        if (isMounted) {
          setInviteCount(0)
        }
      }
    }

    if (user) {
      fetchInviteCount()
    }

    return () => {
      isMounted = false
      if (cacheTimeout) {
        clearTimeout(cacheTimeout)
      }
    }
  }, [user])

  // Stop gift animation after it plays once
  useEffect(() => {
    if (shouldAnimateGift) {
      const timer = setTimeout(() => setShouldAnimateGift(false), 700)
      return () => clearTimeout(timer)
    }
  }, [shouldAnimateGift])

  const handleOrgSwitch = (org) => {
    // Skip a no-op re-select so we don't re-POST the same preference to the server.
    if (org === null) {
      if (!inPersonalMode) clearActiveOrganization()
    } else if (activeOrganization?.id !== org.id) {
      setActiveOrganization(org)
    }
    setShowOrgSwitcher(false)
  }

  // Compute theme state from store values
  const isDark = effectiveTheme === 'dark'
  const isAuto = theme === 'auto'

  const handleLogout = async () => {
    try {
      await logout(queryClient)
      setShowDropdown(false)
    } catch (error) {
      logger.error('Logout failed:', error)
      // Ensure local cleanup even if server call fails
      const { clearAuthData } = useAuthStore.getState()
      clearAuthData(queryClient)
      toast.error('Logout encountered an issue. Your local session has been cleared.')
      setShowDropdown(false)
    }
  }

  return (
    <header
      className={`fixed right-0 left-0 z-[60] h-16 bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 ${isImpersonating ? 'top-10' : 'top-0'}`}
    >
      <div className="h-full px-4 lg:px-6 flex items-center min-w-0">
        <div className="w-full flex items-center justify-between">
          {/* Logo/Brand */}
          <div className="flex items-center space-x-4">
            {/* Hamburger menu - mobile only */}
            <button
              onClick={toggleSidebar}
              className="lg:hidden p-2 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
              aria-label="Toggle menu"
            >
              {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <Link
              to="/"
              className="flex items-center flex-shrink min-w-0 hover:opacity-80 transition-opacity"
            >
              <img
                src={branding.logoUrl || DeaverisLogo}
                alt={branding.name}
                className="w-8 h-8 mr-2 flex-shrink-0"
              />
              {/* Baseline-align the wordmark and taglines so they share a common baseline
                  (the leaf logo stays vertically centered via the parent's items-center). */}
              <div className="flex items-baseline min-w-0">
                {!branding.hideProductName && (
                  <h1 className="text-xl sm:text-2xl font-bold text-primary-600 dark:text-primary-400">
                    {branding.name}
                  </h1>
                )}
                {branding.headerSubtitle && (
                  <span className="hidden sm:inline ml-2 text-sm text-gray-500 dark:text-gray-400">
                    {branding.headerSubtitle}
                  </span>
                )}
                <span className="hidden md:inline ml-3 text-xs text-gray-400 dark:text-gray-500">
                  by {branding.company}
                </span>
              </div>
            </Link>

            {/* Active Organization Badge / Switcher.
                Shown whenever the user belongs to at least one real (non-personal)
                org — including while in Personal Account mode — so the header stays
                in sync with the profile-page switcher. */}
            {showSwitcher && (
              <div className="hidden md:block relative" ref={orgSwitcherRef}>
                <button
                  onClick={() => hasMultipleOrgs && setShowOrgSwitcher(!showOrgSwitcher)}
                  className={`flex items-center space-x-2 px-3 py-1.5 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800 ${
                    hasMultipleOrgs
                      ? 'cursor-pointer hover:bg-primary-100 dark:hover:bg-primary-900/30 transition-colors'
                      : 'cursor-default'
                  }`}
                >
                  {inPersonalMode ? (
                    <User className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  ) : (
                    <Building2 className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                  )}
                  <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                    {inPersonalMode ? personalLabel : activeOrganization.name}
                  </span>
                  {hasMultipleOrgs && (
                    <ChevronDown
                      className={`w-4 h-4 text-primary-500 transition-transform ${showOrgSwitcher ? 'rotate-180' : ''}`}
                    />
                  )}
                </button>

                {/* Organization Switcher Dropdown */}
                {showOrgSwitcher && hasMultipleOrgs && (
                  <div className="absolute top-full mt-2 left-0 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-1 z-50">
                    <div className="px-3 py-2 border-b border-gray-100 dark:border-gray-700">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                        Switch Organization
                      </p>
                    </div>

                    {/* Personal Account (clears the active org) */}
                    {shouldShowPersonalAccount && (
                      <button
                        onClick={() => handleOrgSwitch(null)}
                        className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                          inPersonalMode ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                        }`}
                      >
                        <div className="flex items-center space-x-2">
                          <User className="w-4 h-4 text-gray-400" />
                          <span
                            className={`${
                              inPersonalMode
                                ? 'text-primary-700 dark:text-primary-300 font-medium'
                                : 'text-gray-700 dark:text-gray-300'
                            }`}
                          >
                            {personalLabel}
                          </span>
                        </div>
                        {inPersonalMode && (
                          <Check className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                        )}
                      </button>
                    )}

                    {nonPersonalOrgs.map((org) => {
                      const active = !inPersonalMode && activeOrganization?.id === org.id
                      return (
                        <button
                          key={org.id}
                          onClick={() => handleOrgSwitch(org)}
                          className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors ${
                            active ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                          }`}
                        >
                          <div className="flex items-center space-x-2">
                            {org.type === 'family' ? (
                              <Users className="w-4 h-4 text-gray-400" />
                            ) : (
                              <Building2 className="w-4 h-4 text-gray-400" />
                            )}
                            <span
                              className={`${
                                active
                                  ? 'text-primary-700 dark:text-primary-300 font-medium'
                                  : 'text-gray-700 dark:text-gray-300'
                              }`}
                            >
                              {org.name}
                            </span>
                            <span className="text-xs text-gray-400 dark:text-gray-500 capitalize">
                              ({org.role})
                            </span>
                          </div>
                          {active && (
                            <Check className="w-4 h-4 text-primary-600 dark:text-primary-400" />
                          )}
                        </button>
                      )
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* User menu */}
          <div className="flex items-center space-x-2 sm:space-x-4 flex-shrink-0">
            {/* Notifications */}
            <NotificationBell />

            {/* Messages inbox (direct conversations + pod threads) */}
            <MessagesBell />

            {/* Feedback Button */}
            <button
              onClick={openFeedbackModal}
              className="flex items-center space-x-2 p-3 text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg transition-colors"
              title="Send Feedback"
            >
              <MessageSquare className="w-5 h-5" />
              <span className="hidden sm:inline">Feedback</span>
            </button>

            {/* Invite Friends Button */}
            <button
              onClick={() => navigate('/referrals')}
              className={`relative flex items-center p-3 rounded-lg hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors ${shouldAnimateGift ? 'animate-wiggle' : ''}`}
              title="Invite Friends"
            >
              <Gift className="w-5 h-5 text-primary-500" />

              {/* Invite count badge */}
              {inviteCount !== null && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-primary-500 text-white text-xs font-bold rounded-full flex items-center justify-center px-1">
                  {inviteCount}
                </span>
              )}

              {/* "New" dot - only if not visited */}
              {!hasVisitedReferrals && (
                <span className="absolute -top-0.5 left-0 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white dark:border-gray-800" />
              )}
            </button>

            {/* Theme toggle - gated by dark_mode feature flag */}
            {darkModeEnabled && (
              <div className="relative group">
                <button
                  onClick={toggleTheme}
                  className="p-3 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  aria-label="Toggle theme"
                >
                  {isAuto ? (
                    <Monitor className="w-5 h-5 text-purple-500" />
                  ) : isDark ? (
                    <Sun className="w-5 h-5 text-yellow-500" />
                  ) : (
                    <Moon className="w-5 h-5 text-blue-500" />
                  )}
                </button>
                {/* Tooltip */}
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 px-3 py-2 bg-gray-900 dark:bg-gray-700 text-white text-xs rounded-lg whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none z-50 shadow-lg">
                  <div className="relative">
                    {isAuto ? 'Auto mode (follows system)' : isDark ? 'Dark mode' : 'Light mode'}
                  </div>
                  {/* Arrow */}
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-gray-900 dark:bg-gray-700 rotate-45"></div>
                </div>
              </div>
            )}

            {/* User dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowDropdown(!showDropdown)}
                className="flex items-center space-x-3 text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 p-1"
              >
                <div className="flex items-center space-x-2">
                  <Avatar
                    type={user?.avatarType || 'dicebear'}
                    seed={user?.avatarSeed}
                    style={user?.avatarStyle || 'bottts'}
                    data={user?.avatarData}
                    name={`${user?.firstName || ''} ${user?.lastName || ''}`}
                    publicName={user?.publicName}
                    size="sm"
                  />
                  <div className="text-left hidden sm:block">
                    <p className="font-medium text-gray-900 dark:text-gray-100">
                      {user?.firstName} {user?.lastName}
                    </p>
                  </div>
                </div>
              </button>

              {/* Dropdown menu */}
              {showDropdown && (
                <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50 border border-gray-200 dark:border-gray-700">
                  {/* User info header */}
                  <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center gap-3">
                    <Avatar
                      type={user?.avatarType || 'dicebear'}
                      seed={user?.avatarSeed}
                      style={user?.avatarStyle || 'bottts'}
                      data={user?.avatarData}
                      name={`${user?.firstName || ''} ${user?.lastName || ''}`}
                      publicName={user?.publicName}
                      size="md"
                    />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {user?.firstName} {user?.lastName}
                      </p>
                      <p
                        className="text-xs text-gray-500 dark:text-gray-400 truncate"
                        title={user?.email}
                      >
                        {user?.email}
                      </p>
                    </div>
                  </div>

                  <Link
                    to="/profile"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => setShowDropdown(false)}
                  >
                    <Settings className="w-4 h-4 mr-3" />
                    Profile Settings
                  </Link>

                  <Link
                    to="/referrals"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => setShowDropdown(false)}
                  >
                    <Gift className="w-4 h-4 mr-3 text-primary-500" />
                    Invite Friends
                  </Link>

                  <Link
                    to="/help"
                    className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => setShowDropdown(false)}
                  >
                    <HelpCircle className="w-4 h-4 mr-3" />
                    Help & Support
                  </Link>

                  <div className="border-t border-gray-100 dark:border-gray-700 my-1" />

                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  >
                    <LogOut className="w-4 h-4 mr-3" />
                    Sign out
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}

export default Header
